/**
 * Email-safe HTML generators for the composer's "Insert" helpers.
 *
 * The body textarea stays the single source of truth: these functions only produce a
 * string that is spliced into it, after which the admin can edit it like any other HTML.
 * Everything is inline-styled and table/paragraph based — no flex, grid, classes or JS —
 * because it is rendered by mail clients, not browsers.
 */

/** Inner width of email_templates/layout.html's `.main-content` (640px minus 2×20px padding). */
export const EMAIL_CONTENT_WIDTH = 600;

export const WIDTH_OPTIONS = [25, 50, 75, 100];
export const ALIGN_OPTIONS = ["left", "center", "right"];

/** Brand colours from email_templates/layout.html. */
const BRAND_YELLOW = "#F5C754";
const BRAND_DARK = "#121C24";

export const BUTTON_STYLES = {
  primary: { label: "Yellow (primary)" },
  outline: { label: "Outline" },
};

export const escapeHtml = (value = "") =>
  String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

/**
 * Only plain web links may end up in an email. Rejects javascript:, data:, vbscript:,
 * protocol-relative and anything the URL parser cannot make sense of.
 */
export const isSafeLinkUrl = (value) => {
  if (!value || typeof value !== "string") return false;
  const trimmed = value.trim();
  if (!/^https?:\/\//i.test(trimmed)) return false;
  try {
    const url = new URL(trimmed);
    return (url.protocol === "https:" || url.protocol === "http:") && Boolean(url.hostname);
  } catch {
    return false;
  }
};

const marginForAlign = (align) => {
  if (align === "left") return "0";
  if (align === "right") return "0 0 0 auto";
  return "0 auto";
};

const normaliseAlign = (align) => (ALIGN_OPTIONS.includes(align) ? align : "center");

/**
 * Responsive image that never distorts: the `width` attribute pins a sensible size for
 * clients that ignore CSS (Outlook), while `width:<pct>%; max-width:<px>; height:auto`
 * keeps it fluid and proportional everywhere else.
 */
export const generateImageHtml = ({
  src,
  alt = "",
  widthPercent = 100,
  maxWidthPx,
  align = "center",
  href,
}) => {
  const pct = WIDTH_OPTIONS.includes(Number(widthPercent)) ? Number(widthPercent) : 100;
  const custom = Number(maxWidthPx);
  const maxPx =
    Number.isFinite(custom) && custom > 0
      ? Math.round(custom)
      : Math.round((EMAIL_CONTENT_WIDTH * pct) / 100);
  const alignment = normaliseAlign(align);

  const img =
    `<img src="${escapeHtml(src)}" alt="${escapeHtml(alt)}" width="${maxPx}" ` +
    `style="display:block; width:${pct}%; max-width:${maxPx}px; height:auto; ` +
    `margin:${marginForAlign(alignment)}; border:0;">`;

  const content = isSafeLinkUrl(href)
    ? `<a href="${escapeHtml(href.trim())}" target="_blank" style="display:block; border:0; text-decoration:none;">${img}</a>`
    : img;

  return `<p style="text-align:${alignment}; margin:24px 0;">\n  ${content}\n</p>`;
};

/**
 * "Bulletproof" table button: the <td> carries the background so Outlook paints it, the
 * <a> carries padding so the whole pill is tappable on mobile. Inline `!important` on the
 * colour beats the layout's global `a { color: yellow !important }` rule.
 */
export const generateButtonHtml = ({ text, href, align = "center", style = "primary" }) => {
  const alignment = normaliseAlign(align);
  const outline = style === "outline";
  const label = escapeHtml((text || "").trim() || "Learn more");
  const url = escapeHtml((href || "").trim());

  const tdStyle = outline
    ? `border:2px solid ${BRAND_YELLOW}; border-radius:6px;`
    : `background-color:${BRAND_YELLOW}; border-radius:6px;`;
  const color = outline ? BRAND_YELLOW : BRAND_DARK;
  const bgcolorAttr = outline ? "" : ` bgcolor="${BRAND_YELLOW}"`;

  return (
    `<table role="presentation" cellpadding="0" cellspacing="0" border="0" align="${alignment}" ` +
    `style="margin:24px ${alignment === "center" ? "auto" : alignment === "right" ? "0 24px auto" : "0"};">\n` +
    `  <tr>\n` +
    `    <td align="center"${bgcolorAttr} style="${tdStyle}">\n` +
    `      <a href="${url}" target="_blank" ` +
    `style="display:inline-block; padding:14px 28px; font-family:'Kumbh Sans', Arial, sans-serif; ` +
    `font-size:16px; font-weight:bold; line-height:1.2; color:${color} !important; ` +
    `text-decoration:none; border-radius:6px;">${label}</a>\n` +
    `    </td>\n` +
    `  </tr>\n` +
    `</table>`
  );
};

/**
 * Splices `html` into `body` at the remembered textarea selection, replacing any selected
 * text, and pads with newlines so the snippet lands on its own lines. Returns the new body
 * and where the caret should go afterwards (right after the inserted block).
 */
export const insertHtmlAtSelection = (body, { start, end } = {}, html) => {
  const text = body || "";
  const safeStart = Number.isInteger(start) ? Math.max(0, Math.min(start, text.length)) : text.length;
  const safeEnd = Number.isInteger(end) ? Math.max(safeStart, Math.min(end, text.length)) : safeStart;

  const before = text.slice(0, safeStart);
  const after = text.slice(safeEnd);

  const prefix = before.length === 0 || before.endsWith("\n\n") ? "" : before.endsWith("\n") ? "\n" : "\n\n";
  const suffix = after.length === 0 || after.startsWith("\n\n") ? "" : after.startsWith("\n") ? "\n" : "\n\n";

  const inserted = `${prefix}${html}${suffix}`;
  return {
    body: `${before}${inserted}${after}`,
    caret: before.length + prefix.length + html.length,
  };
};

export const formatBytes = (bytes) => {
  if (!Number.isFinite(bytes)) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};
